import { useSWRConfig } from "swr";
import { useUserService } from "../services";
import { useTokenRefreshHandler } from "../hooks";
import { useActingAsOverrideHeader } from "../auth";
import { Meeting } from "../types";
import { getUserToken } from "src/utils/jwtToken";
import { errorHandler } from "src/hooks/errorHandler";

export const MAX_PER_UPDATE = 10;

type updateMeetingRequest = {
  id: number;
  user?: number;
  contact?: number;
  meetingType?: number;
  enabled?: boolean;
};

export type updateMeetingResponse = {
  failures: any[];
  instances: Meeting[];
};

export function useUpdateMeetings() {
  const accessToken = getUserToken();
  const { mutate, cache } = useSWRConfig();

  const service = useUserService();
  const tokenRefreshHandler = useTokenRefreshHandler();
  const override = useActingAsOverrideHeader();

  const headers: { [index: string]: string } = {
    "JWT-TOKEN": accessToken as string,
  };

  if (override) {
    headers.override = override;
  }

  return async (
    meetings: updateMeetingRequest[],
  ): Promise<updateMeetingResponse> => {
    const batches: updateMeetingRequest[][] = [];

    while (meetings.length) {
      batches.push(meetings.splice(0, MAX_PER_UPDATE));
    }

    const failures: any[] = [];
    const instances: Meeting[] = [];

    for (let i = 0; i < batches.length; i += 1) {
      // we want these to happen sequentially, so we disable the linter for the no-await-in-loop rule
      // the reason -- we want to limit update requests to a maximum of 100 per batch
      // we've found that >~300 will cause issues and potentially even time out
      // in order to reduce server load, we will force 100 per request and force requests to be sequential
      // docs: https://eslint.org/docs/latest/rules/no-await-in-loop
      // eslint-disable-next-line no-await-in-loop
      const response: updateMeetingResponse = await service
        .patch(`/api/meetings/instance`)
        .set(headers)
        .send(batches[i])
        .then(tokenRefreshHandler)
        .then((res: Response) => res.body)
        .then((res: any) => {
          // @ts-ignore
          const keys = cache.keys();

          // Invalidate meeting queries and force refresh.
          Array.from(keys)
            .filter((r: any) => r.includes("/api/meetings/instances"))
            .forEach((k: any) => {
              mutate(k);
            });

          return res;
        })
        .catch(errorHandler);

      if (response.failures?.length > 0) {
        failures.push(...response.failures);
      }

      if (response.instances?.length > 0) {
        instances.push(...response.instances);
      }
    }

    return { failures, instances };
  };
}
